import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Import,
  LayoutGrid,
  LogOut,
  Menu as MenuIcon,
  RefreshCw,
  Rows3,
  Search,
  Settings,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router';
import { useCurrentUser, useLogout, useUpdateProfile } from '../api/auth';
import { useLabels } from '../api/labels';
import { IconButton } from '../components/ui/icon-button';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '../components/ui/menu';
import { Tooltip } from '../components/ui/tooltip';
import { SelectionBar } from '../features/notes/selection-bar';
import { useIsMobile } from '../hooks/use-media-query';
import { cn } from '../lib/cn';
import { filterFromLocation, paths } from '../lib/routes';
import { useUi } from '../stores/ui';

function useViewTitle(): string {
  const location = useLocation();
  const [search] = useSearchParams();
  const { data: labels = [] } = useLabels();
  const filter = filterFromLocation(location.pathname, search);
  if (filter.labelId) return labels.find((l) => l.id === filter.labelId)?.name ?? 'Libellé';
  if (location.pathname.startsWith('/search')) return 'Recherche';
  switch (filter.view) {
    case 'reminders':
      return 'Rappels';
    case 'archive':
      return 'Archives';
    case 'trash':
      return 'Corbeille';
    default:
      return 'Keep';
  }
}

function Logo() {
  return (
    <svg viewBox="0 0 48 48" className="size-10 shrink-0" aria-hidden>
      <path fill="#fbbc04" d="M10 4h20l10 10v28a2 2 0 0 1-2 2H10a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z" />
      <path fill="#f29900" d="M30 4v8a2 2 0 0 0 2 2h8z" />
      <path
        fill="#fff"
        d="M24 17a8 8 0 0 0-4.5 14.6V35a1.5 1.5 0 0 0 1.5 1.5h6a1.5 1.5 0 0 0 1.5-1.5v-3.4A8 8 0 0 0 24 17z"
      />
      <rect x="21" y="38" width="6" height="2" rx="1" fill="#fff" />
    </svg>
  );
}

/** Champ de recherche synchronisé avec l'URL (/search?q=…). */
function SearchBox({ onClose }: { onClose?: () => void }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const onSearchPage = location.pathname.startsWith(paths.search);
  const [value, setValue] = useState(onSearchPage ? (params.get('q') ?? '') : '');
  const inputRef = useRef<HTMLInputElement>(null);

  // Quitter la page de recherche vide le champ.
  useEffect(() => {
    if (!onSearchPage) setValue('');
  }, [onSearchPage]);

  // Raccourci « / » pour rechercher, comme dans Keep.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      if (event.key === '/' && !target.closest('input, textarea, [contenteditable="true"]')) {
        event.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // La recherche suit la saisie, avec un léger délai pour ne pas surcharger l'API.
  useEffect(() => {
    if (!onSearchPage) return;
    const timer = setTimeout(() => {
      const next = new URLSearchParams(params);
      if (value.trim()) next.set('q', value);
      else next.delete('q');
      if (next.toString() !== params.toString())
        navigate(`${paths.search}?${next}`, { replace: true });
    }, 250);
    return () => clearTimeout(timer);
  }, [value, onSearchPage, params, navigate]);

  const close = () => {
    setValue('');
    if (onSearchPage) navigate(paths.notes);
    onClose?.();
  };

  return (
    <form
      role="search"
      className="group flex h-12 w-full max-w-3xl items-center rounded-lg bg-search px-1 transition-shadow focus-within:bg-bg focus-within:shadow-raised"
      onSubmit={(event) => {
        event.preventDefault();
        navigate(`${paths.search}?q=${encodeURIComponent(value)}`);
      }}
    >
      <IconButton icon={Search} label="Rechercher" type="submit" />
      <input
        ref={inputRef}
        value={value}
        onChange={(event) => setValue(event.target.value)}
        onFocus={() => {
          if (!onSearchPage)
            navigate(value ? `${paths.search}?q=${encodeURIComponent(value)}` : paths.search);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            close();
            inputRef.current?.blur();
          }
        }}
        placeholder="Rechercher"
        aria-label="Rechercher dans les notes"
        className="bare-input h-full px-2 text-base"
      />
      {(value || onSearchPage) && (
        <IconButton icon={X} label="Effacer la recherche" onClick={close} />
      )}
    </form>
  );
}

function AccountMenu() {
  const user = useCurrentUser();
  const logout = useLogout();
  const openSettings = useUi((s) => s.setSettingsOpen);
  const openImport = useUi((s) => s.setImportOpen);
  const navigate = useNavigate();
  const initial = (user.name || user.email).charAt(0).toUpperCase();

  return (
    <Menu>
      <Tooltip label={`${user.name} (${user.email})`}>
        <MenuTrigger asChild>
          <button
            type="button"
            aria-label="Compte"
            className="ml-1 flex size-10 items-center justify-center rounded-full hover:bg-hover"
          >
            <span className="flex size-8 items-center justify-center rounded-full bg-[#7b1fa2] text-sm font-medium text-white">
              {initial}
            </span>
          </button>
        </MenuTrigger>
      </Tooltip>
      <MenuContent align="end" className="w-64">
        <div className="border-b border-line px-4 pt-2 pb-3">
          <div className="truncate font-medium">{user.name}</div>
          <div className="truncate text-xs text-muted">{user.email}</div>
        </div>
        <MenuItem onSelect={() => openSettings(true)}>
          <Settings size={18} className="mr-3 text-muted" /> Paramètres
        </MenuItem>
        <MenuItem onSelect={() => openImport(true)}>
          <Import size={18} className="mr-3 text-muted" /> Importer depuis Google Keep
        </MenuItem>
        <MenuItem
          onSelect={() =>
            logout.mutate(undefined, { onSettled: () => navigate(paths.login, { replace: true }) })
          }
        >
          <LogOut size={18} className="mr-3 text-muted" /> Se déconnecter
        </MenuItem>
      </MenuContent>
    </Menu>
  );
}

export function Header() {
  const title = useViewTitle();
  const isMobile = useIsMobile();
  const toggleSidebar = useUi((s) => s.toggleSidebar);
  const openSettings = useUi((s) => s.setSettingsOpen);
  const selectionCount = useUi((s) => s.selection.size);
  const user = useCurrentUser();
  const updateProfile = useUpdateProfile();
  const queryClient = useQueryClient();
  const [refreshing, setRefreshing] = useState(false);
  const [mobileSearch, setMobileSearch] = useState(false);
  const location = useLocation();
  const listLayout = user.preferences.layout === 'list';

  // Sur mobile, la recherche occupe tout l'en-tête tant qu'on est sur la page de recherche.
  useEffect(() => {
    setMobileSearch(location.pathname.startsWith(paths.search));
  }, [location.pathname]);

  const refresh = async () => {
    setRefreshing(true);
    await queryClient.invalidateQueries();
    setTimeout(() => setRefreshing(false), 400);
  };

  if (selectionCount > 0) return <SelectionBar />;

  if (isMobile && mobileSearch) {
    return (
      <header className="fixed inset-x-0 top-0 z-40 flex h-16 items-center gap-1 border-b border-line bg-bg px-2">
        <IconButton
          icon={ArrowLeft}
          label="Fermer la recherche"
          onClick={() => setMobileSearch(false)}
        />
        <SearchBox onClose={() => setMobileSearch(false)} />
      </header>
    );
  }

  return (
    <header className="fixed inset-x-0 top-0 z-40 flex h-16 items-center gap-1 border-b border-line bg-bg px-2 sm:px-3">
      <IconButton
        icon={MenuIcon}
        label="Menu principal"
        size="lg"
        onClick={() => toggleSidebar(isMobile)}
      />
      <div
        className={cn('flex min-w-0 items-center gap-1 pr-2', !isMobile && 'w-[218px] shrink-0')}
      >
        {title === 'Keep' && <Logo />}
        <h1 className="truncate pl-1 text-[22px] leading-none text-muted">{title}</h1>
      </div>
      <div className="flex flex-1 justify-center max-md:hidden">
        <SearchBox />
      </div>
      <div className="ml-auto flex items-center">
        {isMobile && (
          <IconButton icon={Search} label="Rechercher" onClick={() => setMobileSearch(true)} />
        )}
        <IconButton
          icon={RefreshCw}
          label="Actualiser"
          onClick={refresh}
          className={cn(refreshing && '[&_svg]:animate-spin')}
        />
        {!isMobile && (
          <IconButton
            icon={listLayout ? LayoutGrid : Rows3}
            label={listLayout ? 'Affichage grille' : 'Affichage liste'}
            onClick={() =>
              updateProfile.mutate({ preferences: { layout: listLayout ? 'grid' : 'list' } })
            }
          />
        )}
        <IconButton
          icon={Settings}
          label="Paramètres"
          onClick={() => openSettings(true)}
          className="max-sm:hidden"
        />
        <AccountMenu />
      </div>
    </header>
  );
}
